// Settings → Privacy: the right-to-erasure tool (T4). Owner and Admin, on
// EVERY plan — honouring a respondent's erasure request is a legal
// obligation, not a feature to sell (decision 7). Preview first, then a
// type-it-again confirmation, then the hard delete; the screen names what
// cannot be recalled.
import { useEffect, useState } from 'react';
import {
  ErasurePreview, ErasureRequest, FileLinkSettings, aiPrivacyApi, eraseRespondentApi, erasurePreviewApi, fileLinksApi,
  privacyRequestsApi, setAiPrivacyApi, setFileLinksApi,
} from '../../api/admin';
import { useToast } from '../../components/Toast';
import { TableCard } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';

type Done = { submissions: number; invites: number; invitations: number; links: number; childrenScrubbed: number };

export default function PrivacySettings() {
  const [email, setEmail] = useState('');
  const [preview, setPreview] = useState<ErasurePreview | null>(null);
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState<Done | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** bumped after an erasure so Past requests shows it at once */
  const [erased, setErased] = useState(0);

  async function onPreview(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(''); setDone(null); setPreview(null); setConfirm('');
    try {
      setPreview((await erasurePreviewApi(email.trim())).preview);
    } catch (err: any) {
      setError(err.message || 'Could not search for that address.');
    } finally { setBusy(false); }
  }

  async function onErase() {
    if (!preview) return;
    setBusy(true); setError('');
    try {
      const r = await eraseRespondentApi(preview.email, confirm.trim());
      setDone(r);
      setErased((n) => n + 1);
      setPreview(null);
      setEmail(''); setConfirm('');
    } catch (err: any) {
      setError(err.message || 'The erasure did not run.');
    } finally { setBusy(false); }
  }

  return (
    <div className="st-col">
    <div className="fs-page-head">
      <div className="text">
        <h1>Privacy</h1>
        <div className="fs-page-sub">
          Find someone, see exactly what will be erased, then erase it. Every request is kept on record.
        </div>
      </div>
    </div>
    <section className="fs-card st-card" aria-labelledby="st-erase">
      <div className="st-card-head"><h2 id="st-erase">Right to erasure</h2></div>
      <div className="st-card-body">
      <p className="st-muted" style={{ fontSize: 13.5, margin: '0 0 16px', lineHeight: 1.55 }}>
        When a respondent asks you to delete their data, this finds everything this account holds
        for their email address — submissions naming it (with every uploaded file and signature),
        invitations, and sign-in links — and deletes it permanently. Answers other people carried
        over from an erased submission are scrubbed too. What it <b>cannot</b> recall: deliveries
        already sent to your integrations and inboxes, and exports already downloaded.
      </p>
      {error && <div className="ad-err">{error}</div>}

      {done ? (
        <div className="fs-consequence">
          Erased: {done.submissions} submission{done.submissions === 1 ? '' : 's'} (with their files),{' '}
          {done.invites + done.invitations} invitation{done.invites + done.invitations === 1 ? '' : 's'} and {done.links} sign-in
          link{done.links === 1 ? '' : 's'}
          {done.childrenScrubbed
            ? <>; carried answers removed from {done.childrenScrubbed} continuing submission{done.childrenScrubbed === 1 ? '' : 's'}</>
            : null}.
          The trail records the counts. Remember any copies your own integrations hold.
        </div>
      ) : null}

      <form onSubmit={onPreview} style={{ maxWidth: 420 }}>
        <div className="ad-field">
          <label htmlFor="erasure-email">Respondent&rsquo;s email address</label>
          <input
            id="erasure-email" type="text" inputMode="email" value={email}
            onChange={(e) => { setEmail(e.target.value); setPreview(null); }}
          />
        </div>
        <button className="fs-btn" type="submit" disabled={busy || !email.trim()}>
          {busy && !preview ? 'Searching…' : 'Find their data'}
        </button>
      </form>

      {preview && (
        <div style={{ marginTop: 18, maxWidth: 560 }}>
          {preview.totalSubmissions === 0 && preview.invites === 0 && preview.invitations === 0 && preview.links === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13.5 }}>
              Nothing stored for <b>{preview.email}</b> — no submissions, invitations or links name it.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
                Stored for <b>{preview.email}</b>:
                <ul style={{ margin: '6px 0 10px', paddingLeft: 20 }}>
                  {preview.matches.map((m) => (
                    <li key={m.formId}>{m.submissions} submission{m.submissions === 1 ? '' : 's'} on “{m.formName}”</li>
                  ))}
                  {preview.invites > 0 && <li>{preview.invites} invitation{preview.invites === 1 ? '' : 's'}</li>}
                  {preview.invitations > 0 && <li>{preview.invitations} emailed form link{preview.invitations === 1 ? '' : 's'}</li>}
                  {preview.links > 0 && <li>{preview.links} sign-in link{preview.links === 1 ? '' : 's'}</li>}
                </ul>
              </div>
              <div className="fs-consequence" style={{ marginBottom: 12 }}>
                Deleting is permanent and includes every uploaded file and signature. There is no
                undo, and nothing already delivered or exported comes back.
              </div>
              <div className="ad-field">
                <label htmlFor="erasure-confirm">Type the address again to confirm</label>
                <input id="erasure-confirm" type="text" inputMode="email" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <button
                className="fs-btn danger" disabled={busy || confirm.trim().toLowerCase() !== preview.email}
                onClick={() => void onErase()}
              >
                {busy ? 'Erasing…' : 'Erase everything for this address'}
              </button>
            </>
          )}
        </div>
      )}
      </div>
    </section>
    <PastRequests refresh={erased} />
    <AiSubmissionAccess />
    <FileLinks />
    </div>
  );
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What one erasure removed, in the words the result line uses. */
export function erasedWhat(r: Pick<ErasureRequest, 'submissions' | 'invites' | 'links'>): string {
  const parts = [plural(r.submissions, 'response')];
  if (r.invites) parts.push(plural(r.invites, 'invitation'));
  if (r.links) parts.push(plural(r.links, 'link'));
  return parts.join(' · ');
}

/**
 * Past requests (R10, playbook §4): every erasure this account has run, read
 * back from the audit entry the erasure itself writes. Read-only — there is
 * no receipt document to offer, so none is promised.
 */
function PastRequests({ refresh }: { refresh: number }) {
  const [rows, setRows] = useState<ErasureRequest[] | null>(null);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const phone = useMediaQuery(PHONE);

  async function load(before?: number) {
    setBusy(true); setError('');
    try {
      const r = await privacyRequestsApi(before);
      setRows((cur) => (before && cur ? [...cur, ...r.requests] : r.requests));
      setNextBefore(r.nextBefore);
    } catch (e: any) {
      setError(e.message || 'Could not load past requests.');
      if (!before) setRows([]);
    } finally { setBusy(false); }
  }
  useEffect(() => { void load(); }, [refresh]);

  return (
    <TableCard
      title="Past requests"
      hint="Every erasure run on this account: who asked to be forgotten, what went, and who ran it."
      footer={nextBefore !== null ? (
        <button className="fs-btn sm" disabled={busy} onClick={() => void load(nextBefore)}>
          {busy ? 'Loading…' : 'Load more'}
        </button>
      ) : undefined}
    >
      {error && <div className="st-card-body"><div className="ad-err">{error}</div></div>}
      {!rows ? (
        <div className="st-card-body st-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="st-card-body st-muted">No erasures yet.</div>
      ) : phone ? (
        <div className="ic-cards">
          {rows.map((r) => (
            <div className="ic-card" key={r.id}>
              <b>{r.email || '—'}</b>
              <div className="facts">{erasedWhat(r)} · {day(r.at)} · by {r.by}</div>
            </div>
          ))}
        </div>
      ) : (
        <table className="fs-table">
          <thead><tr><th>Person</th><th>What</th><th>When</th><th>By</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.email || '—'}</td>
                <td>{erasedWhat(r)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{day(r.at)}</td>
                <td className="st-muted">{r.by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </TableCard>
  );
}

/**
 * How long a file link opens for anybody holding it (finding 96; Ben,
 * 2026-10-09). The Owner's choice: the standard lifetime, their own number of
 * days, or never asking for a sign-in. Last on the page, so nothing above it
 * moves. Until Form-Magic switches the lifetime on, the card says so — the
 * choice is made ahead of time and changes nothing yet.
 */
function FileLinks() {
  const [s, setS] = useState<FileLinkSettings | null>(null);
  const [mode, setMode] = useState<FileLinkSettings['mode']>('default');
  const [days, setDays] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const take = (f: FileLinkSettings) => { setS(f); setMode(f.mode); setDays(String(f.days ?? f.defaultDays)); };
  useEffect(() => { fileLinksApi().then((r) => take(r.fileLinks)).catch(() => setS(null)); }, []);
  if (!s) return null;

  const typed = Number(days);
  const daysOk = Number.isInteger(typed) && typed >= 1 && typed <= s.maxDays;
  const changed = mode !== s.mode || (mode === 'days' && typed !== s.days);
  const plural = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

  async function save() {
    setBusy(true); setError('');
    try {
      take((await setFileLinksApi(mode === 'days' ? { mode, days: typed } : { mode })).fileLinks);
      toast('File link lifetime saved.');
    } catch (err: any) {
      setError(err.message || 'The setting was not saved.');
    } finally { setBusy(false); }
  }

  return (
    <section className="fs-card st-card" aria-labelledby="st-file-links">
      <div className="st-card-head"><h2 id="st-file-links">File links</h2></div>
      <div className="st-card-body">
      <p className="st-muted" style={{ fontSize: 13.5, margin: '0 0 16px', lineHeight: 1.55 }}>
        Every file a respondent uploads, every signature and every signed PDF is reached by a link, and those
        links end up in your integrations, tickets and emails. For a set time after the file is uploaded the
        link opens for anyone who has it. After that it asks for a Form-Magic sign-in, and opens for people
        on your team who can see the form. Logos and images on your forms are not affected.
      </p>
      {!s.enforced && (
        <div className="fs-consequence" style={{ marginBottom: 16 }}>
          Not in force yet: today every file link still opens for anyone, with no time limit. Choose now, and
          your choice applies from the day Form-Magic switches this on.
        </div>
      )}
      {error && <div className="ad-err">{error}</div>}
      <div role="radiogroup" aria-labelledby="st-file-links">
        <label className={`fs-choice${mode === 'default' ? ' on' : ''}`}>
          <input type="radio" name="st-file-links" checked={mode === 'default'} disabled={!s.canChange || busy} onChange={() => setMode('default')} />
          <span>
            <b>Standard — {plural(s.defaultDays)}</b>
            <small>Form-Magic&rsquo;s own setting. If it changes, yours follows.</small>
          </span>
        </label>
        <label className={`fs-choice${mode === 'days' ? ' on' : ''}`}>
          <input type="radio" name="st-file-links" checked={mode === 'days'} disabled={!s.canChange || busy} onChange={() => setMode('days')} />
          <span>
            <b>Your own number of days</b>
            <small>Counted from when each file was uploaded.</small>
          </span>
        </label>
        {mode === 'days' && (
          <div className="fs-choice-body">
            <div className="fs-field" style={{ maxWidth: 160 }}>
              <label htmlFor="st-file-link-days">Days</label>
              <input
                id="st-file-link-days" type="number" inputMode="numeric" min={1} max={s.maxDays} value={days}
                disabled={!s.canChange || busy} onChange={(e) => setDays(e.target.value)}
              />
            </div>
          </div>
        )}
        <label className={`fs-choice${mode === 'never' ? ' on' : ''}`}>
          <input type="radio" name="st-file-links" checked={mode === 'never'} disabled={!s.canChange || busy} onChange={() => setMode('never')} />
          <span>
            <b>Never ask for a sign-in</b>
            <small>
              Every file link opens for anyone who has it, for as long as the file exists. Files uploaded
              before October 2026 have names that could be guessed with enough tries; this choice leaves
              those open too.
            </small>
          </span>
        </label>
      </div>
      {mode === 'days' && !daysOk && (
        <div className="ad-err" style={{ marginTop: 12 }}>Enter a whole number of days from 1 to {s.maxDays.toLocaleString()}.</div>
      )}
      {s.canChange ? (
        <div style={{ marginTop: 16 }}>
          <button className="fs-btn primary" disabled={busy || !changed || (mode === 'days' && !daysOk)} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : (
        <p className="st-muted" style={{ fontSize: 13, margin: '14px 0 0' }}>Only an Owner can change this.</p>
      )}
      </div>
    </section>
  );
}

/**
 * Whether "Ask AI" inside a form may read its submissions (068). Off until an
 * Owner or Admin turns it on: when it is on, the answers a question needs are
 * sent to the AI provider the platform uses. The card sits under the erasure
 * tool, so nothing above it moves.
 */
function AiSubmissionAccess() {
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  useEffect(() => {
    aiPrivacyApi().then((r) => setOn(r.submissionAccess)).catch(() => setOn(false));
  }, []);

  async function change(next: boolean) {
    setBusy(true); setError('');
    try {
      setOn((await setAiPrivacyApi(next)).submissionAccess);
      toast(next ? 'The AI assistant can now read submissions.' : 'The AI assistant can no longer read submissions.');
    } catch (err: any) {
      setError(err.message || 'The setting was not saved.');
    } finally { setBusy(false); }
  }

  return (
    <section className="fs-card st-card" aria-labelledby="st-ai">
      <div className="st-card-head"><h2 id="st-ai">The AI assistant and your submissions</h2></div>
      <div className="st-card-body">
      <p className="st-muted" style={{ fontSize: 13.5, margin: '0 0 16px', lineHeight: 1.55 }}>
        &ldquo;Ask AI&rdquo; inside a form can answer questions about its submissions — how many came in,
        what people chose, which deliveries failed. To do that, the answers a question needs are sent
        to the AI provider this service uses. Anyone who can
        see a form&rsquo;s submissions can ask. Each question that reads them is recorded in the Audit log.
      </p>
      {error && <div className="ad-err">{error}</div>}
      <label className="fs-check">
        <input
          type="checkbox" checked={!!on} disabled={on === null || busy}
          onChange={(e) => { void change(e.target.checked); }}
        />
        <span>
          <b>Let the AI assistant read submissions</b>
          <small>Off: it still helps with building and troubleshooting forms, but cannot see any answer.</small>
        </span>
      </label>
      </div>
    </section>
  );
}
